import { supabase } from '../integrations/supabase/client';
import { getAppEnvironment } from '../utils/environment';

export interface ValidateUserRequest {
  Email?: string;
  Telefono?: string;
  IdMarca: number;
}

export interface ValidateOtpRequest {
  Email?: string;
  Telefono?: string;
  IdMarca: number;
  otp: string;
}

export interface ResendOtpRequest {
  Email?: string;
  Telefono?: string;
  IdMarca: number;
}

/**
 * Valida el usuario llamando al Edge Function user-validation
 * Acepta Email O Telefono (al menos uno requerido)
 */
export async function validateUser(data: ValidateUserRequest): Promise<void> {
  const identifier = data.Email || data.Telefono;
  console.log('[Auth] Validating user:', identifier);
  
  // Construir body con los parámetros presentes
  const requestBody: Record<string, any> = { IdMarca: data.IdMarca };
  if (data.Email) requestBody.Email = data.Email;
  if (data.Telefono) requestBody.Telefono = data.Telefono.replace(/^\+/, '');

  const { data: responseData, error } = await supabase.functions.invoke('user-validation', {
    body: { ...requestBody, environment: getAppEnvironment() }
  });

  if (error) {
    console.error('[Auth] Edge Function error:', error);
    throw new Error('serverError');
  }

  // Verificar respuesta encapsulada
  if (!responseData?.success) {
    // Buscar mensaje en minúscula y mayúscula para compatibilidad
    const mensaje = responseData?.data?.mensaje || responseData?.data?.Mensaje || responseData?.error || '';
    const mensajeLower = mensaje.toLowerCase();
    
    console.log('[Auth] Error from API:', { status: responseData?.status, mensaje });
    
    // Mapear mensajes del servidor a keys de traducción
    if (mensajeLower.includes('marca no existe') || mensajeLower.includes('marca invalida')) {
      throw new Error('brandNotFound');
    }
    
    if (mensajeLower.includes('usuario no encontrado')) {
      throw new Error('userNotFound');
    }
    
    // Teléfono no encontrado - múltiples variantes con/sin acentos
    if (mensajeLower.includes('telefono no encontrado') || 
        mensajeLower.includes('teléfono no encontrado') ||
        mensajeLower.includes('número telefónico no está registrado') ||
        mensajeLower.includes('numero telefonico no esta registrado') ||
        mensajeLower.includes('número telefonico no esta registrado') ||
        mensajeLower.includes('numero telefónico no está registrado') ||
        (mensajeLower.includes('telefon') && mensajeLower.includes('no est') && mensajeLower.includes('registrad'))) {
      throw new Error('phoneNotFound');
    }
    
    // Correo no encontrado - múltiples variantes
    if (mensajeLower.includes('correo no encontrado') ||
        mensajeLower.includes('correo electrónico no está registrado') ||
        mensajeLower.includes('correo electronico no esta registrado') ||
        (mensajeLower.includes('correo') && mensajeLower.includes('no est') && mensajeLower.includes('registrad'))) {
      throw new Error('emailNotFound');
    }
    
    if ((mensajeLower.includes('email') || mensajeLower.includes('correo')) && (mensajeLower.includes('invalido') || mensajeLower.includes('invalid') || mensajeLower.includes('no válido'))) {
      throw new Error('invalidEmail');
    }
    
    if (mensajeLower.includes('telefono') && (mensajeLower.includes('invalido') || mensajeLower.includes('invalid') || mensajeLower.includes('no válido'))) {
      throw new Error('phoneInvalid');
    }
    
    // Error al enviar código de verificación
    if (mensajeLower.includes('enviar el código') || mensajeLower.includes('enviar el codigo')) {
      throw new Error('otpSendError');
    }
    
    // Cualquier otro 404
    if (responseData?.status === 404) {
      throw new Error('userNotFound');
    }
    
    throw new Error('serverError');
  }
  
  console.log('[Auth] User validated successfully');
}

/**
 * Mapea mensajes de error del API a keys de traducción
 */
function mapOtpError(mensaje: string, status: number): string {
  const msg = mensaje.toLowerCase();
  
  // Errores 400
  if (msg.includes('email requerido')) return 'emailRequired';
  if (msg.includes('formato de correo') && msg.includes('no válido')) return 'invalidEmailFormat';
  if (msg.includes('marca no válida') || msg.includes('marca invalida')) return 'invalidBrand';
  if (msg.includes('marca inactiva')) return 'brandInactive';
  if (msg.includes('usuario no activo')) return 'userInactive';
  if (msg.includes('otp vencido') || msg.includes('expirado')) return 'otpExpired';
  if (msg.includes('código de verificación') && msg.includes('incorrecto')) return 'otpInvalid';
  if (msg.includes('codigo de verificacion') && msg.includes('incorrecto')) return 'otpInvalid';
  if (msg.includes('excedido') && msg.includes('intentos')) return 'maxAttemptsExceeded';
  if (msg.includes('otp no válido') || msg.includes('otp invalid')) return 'otpInvalid';
  if (msg.includes('otp ya fue usado')) return 'otpAlreadyUsed';
  if (msg.includes('no es posible reenviar') || msg.includes('cannot resend')) return 'otpResendNotAllowed';
  
  // Errores 404
  if (msg.includes('usuario no encontrado')) return 'userNotFound';
  if (msg.includes('marca no existe')) return 'brandNotFound';
  if (msg.includes('número telefónico no está registrado') || 
      msg.includes('numero telefonico no esta registrado') ||
      (msg.includes('telefon') && msg.includes('no est') && msg.includes('registrad'))) return 'phoneNotFound';
  if (msg.includes('correo no encontrado') ||
      msg.includes('correo electrónico no está registrado') ||
      (msg.includes('correo') && msg.includes('no est') && msg.includes('registrad'))) return 'emailNotFound';
  if (msg.includes('no registra una solicitud de pago')) return 'noValidPaymentRequest';
  
  // Errores 500
  if (msg.includes('error interno') || msg.includes('procesar el otp')) return 'otpProcessingError';
  if (msg.includes('enviar el código')) return 'otpSendError';
  
  return 'serverError';
}

export interface OtpValidationResult {
  token: string;
  email: string;
  telefono?: string;
  marca?: string;
}

/**
 * Valida el OTP ingresado por el usuario
 */
export async function validateOtp(data: ValidateOtpRequest): Promise<OtpValidationResult> {
  const identifier = data.Email || data.Telefono;
  console.log('[Auth] Validating OTP for:', identifier);
  
  // Construir body con los parámetros presentes
  const requestBody: Record<string, any> = { 
    IdMarca: data.IdMarca,
    Otp: data.otp
  };
  if (data.Email) requestBody.Email = data.Email;
  if (data.Telefono) requestBody.Telefono = data.Telefono.replace(/^\+/, '');

  const { data: responseData, error } = await supabase.functions.invoke('otp-validation', {
    body: { ...requestBody, environment: getAppEnvironment() }
  });

  if (error) {
    console.error('[Auth] Edge Function error:', error);
    throw new Error('serverError');
  }

  // Verificar respuesta encapsulada
  if (!responseData?.success) {
    const mensaje = responseData?.data?.mensaje || responseData?.data?.Mensaje || responseData?.error || '';
    const status = responseData?.status || 400;
    
    console.log('[Auth] Error from API:', { status, mensaje });
    
    const errorKey = mapOtpError(mensaje, status);
    throw new Error(errorKey);
  }
  
  console.log('[Auth] OTP validated successfully');
  
  return {
    token: responseData.data.Token,
    email: responseData.data.Email,
    telefono: responseData.data.Telefono,
    marca: responseData.data.Marca
  };
}

/**
 * Reenvía el OTP al correo/teléfono del usuario
 */
export async function resendOtp(data: ResendOtpRequest): Promise<void> {
  const identifier = data.Email || data.Telefono;
  console.log('[Auth] Resending OTP to:', identifier);
  
  // Construir body con los parámetros presentes
  const requestBody: Record<string, any> = { IdMarca: data.IdMarca };
  if (data.Email) requestBody.Email = data.Email;
  if (data.Telefono) requestBody.Telefono = data.Telefono.replace(/^\+/, '');

  const { data: responseData, error } = await supabase.functions.invoke('otp-resend', {
    body: { ...requestBody, environment: getAppEnvironment() }
  });

  if (error) {
    console.error('[Auth] Edge Function error:', error);
    throw new Error('serverError');
  }

  // Verificar respuesta encapsulada
  if (!responseData?.success) {
    const mensaje = responseData?.data?.mensaje || responseData?.data?.Mensaje || responseData?.error || '';
    const status = responseData?.status || 400;
    
    console.log('[Auth] Error from API:', { status, mensaje });
    
    const errorKey = mapOtpError(mensaje, status);
    throw new Error(errorKey);
  }
  
  console.log('[Auth] OTP resent successfully');
}
